'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, Fetchable } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { InterfaceChainUtils, type InterfaceInputToken, type Point, type Rewards } from '@morpho-org/dapp-core';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { ChainConfig } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import type { CardNetApyRowItemProps } from './CardNetApyRowItem/CardNetApyRowItem';
import { CardNetApyAggregatedRows } from './CardNetApyAggregatedRows';
import { CardMessage } from './states';
export type CardNetApyVariant = 'market' | 'vault';
export interface NetApyHoverCardProps {
  nativeApy?: bigint | null;
  netApy?: bigint | null;
  performanceFee?: bigint | null;
  managementFee?: bigint | null;
  rewards?: Rewards[] | null;
  asset?: InterfaceInputToken | null;
  points?: Point[];
  maxApy?: number | null;
  isRateCapped?: boolean;
  includesDonations?: boolean;
  /** Set for V2 vaults (null when the split is unknown); undefined keeps the V1 card. */
  apyBreakdown?: VaultV2ApyBreakdown | null;
}
export interface NetApyMarket {
  loanSymbol: string;
  loanLogoURI: Fetchable<string>;
  collateralSymbol: string;
  collateralLogoURI: Fetchable<string>;
}
export interface NetApy {
  name: string;
  apy: bigint;
  weight: bigint;
  allocation: bigint;
  percentageOffset: bigint;
  chainId?: ChainId;
  vaultAddress?: Address;
  logoURIs?: Fetchable<string>[];
  market?: NetApyMarket;
  isVaultV2?: boolean;
  maxRate?: bigint | null;
  href?: string;
  hoverCardProps?: NetApyHoverCardProps;
}
export interface RewardTokenItem {
  name: string;
  apr: bigint;
  weight: bigint;
  chainId?: ChainId;
  logoURIs?: Fetchable<string>[];
  market?: NetApyMarket;
  allocation: bigint;
  percentageOffset: bigint;
  isVaultV2?: boolean;
}
export interface RewardToken {
  totalApr: bigint;
  asset: {
    symbol: string;
    logoURI: string;
  };
  items: RewardTokenItem[];
}
export interface AggregatedApy {
  apy: bigint;
  itemApy: bigint;
  rewardTokens: RewardToken[];
  itemApys: NetApy[];
  className?: string;
}
export interface CardNetApyProps {
  aggregatedApy: AggregatedApy | null;
  variant: CardNetApyVariant;
  chainIds?: ChainId[];
  apyTooltipContent?: CardNetApyRowItemProps['tooltipContent'];
  error?: Error | boolean | undefined;
  hasNoPositions?: boolean;
}
const BREAKDOWN_TITLE: Record<CardNetApyVariant, string> = {
  vault: 'APY breakdown',
  market: 'Rate breakdown'
};
const getNoPositionsMessage = (variant: CardNetApyVariant, chainIds: ChainId[] | undefined, targetChainName: string) => {
  const type = variant === 'market' ? 'Borrow' : 'Earn';
  if (!chainIds) {
    return `You currently have no ${type} position on ${targetChainName}.`;
  }
  const isAllChains = chainIds.length >= ChainConfig.AVAILABLE_CHAIN_IDS.length;
  if (isAllChains) {
    return `You currently have no ${type} position.`;
  }
  const chainNames = chainIds.map(id => InterfaceChainUtils.getMetadata(id).chain.name);
  return `You currently have no ${type} position on ${chainNames.join(', ')}.`;
};
const CardNetApy = t0 => {
  const $ = _c(12);
  const {
    aggregatedApy,
    variant,
    chainIds,
    apyTooltipContent,
    error,
    hasNoPositions
  } = t0;
  const {
    targetChain
  } = useWeb3Context();
  if (error) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CardMessage message="Something went wrong." />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (hasNoPositions || !aggregatedApy) {
    let t1;
    if ($[1] !== chainIds || $[2] !== targetChain.name || $[3] !== variant) {
      t1 = getNoPositionsMessage(variant, chainIds, targetChain.name);
      $[1] = chainIds;
      $[2] = targetChain.name;
      $[3] = variant;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <CardMessage message={t1} />;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const t1 = BREAKDOWN_TITLE[variant];
  let t2;
  if ($[7] !== aggregatedApy.itemApys || $[8] !== apyTooltipContent || $[9] !== t1 || $[10] !== variant) {
    t2 = <div className="max-xl:pb-md max-h-[340px] overflow-auto max-xl:max-h-none"><CardNetApyAggregatedRows apys={aggregatedApy.itemApys} title={t1} tooltipContent={apyTooltipContent} variant={variant} /></div>;
    $[7] = aggregatedApy.itemApys;
    $[8] = apyTooltipContent;
    $[9] = t1;
    $[10] = variant;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
export default CardNetApy;